import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { apiFetch } from "@/lib/api-client";
import { formatCurrency, formatDateTime } from "@/lib/i18n";

interface PublicEstimateTier {
  id: string;
  name: string;
  description?: string;
  totalValueCents: number;
  isRecommended?: boolean;
}

interface PublicEstimateSection {
  id: string;
  title: string;
  sectionType: string;
  pricing?: Array<{
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }> | null;
}

interface PublicEstimateResponse {
  data: {
    proposalId: string;
    title: string;
    currency: string;
    validUntil: string | null;
    status: string;
    signerName: string;
    optionTiers: PublicEstimateTier[];
    sections: PublicEstimateSection[];
    service: {
      serviceName: string;
      desiredStartAt: string;
      durationMinutes: number;
    } | null;
  };
}

function formatMoney(cents: number, currency: string): string {
  return formatCurrency(cents / 100, currency);
}

export default function PublicEstimatePage() {
  const { tenantSlug, proposalId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") ?? "";
  const [detail, setDetail] = useState<PublicEstimateResponse["data"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [decisionState, setDecisionState] = useState<"idle" | "accepting" | "declining" | "accepted" | "declined">("idle");
  const [selectedOptionId, setSelectedOptionId] = useState("");
  const [signerName, setSignerName] = useState("");
  const [signatureText, setSignatureText] = useState("");

  useEffect(() => {
    if (!tenantSlug || !proposalId || !token) {
      setError("The estimate link is incomplete.");
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const response = await apiFetch<PublicEstimateResponse>(
          `/api/public/bookings/estimates/${tenantSlug}/${proposalId}?token=${encodeURIComponent(token)}`,
        );
        if (cancelled) return;
        const nextDetail = response.data;
        setDetail(nextDetail);
        const recommended = nextDetail.optionTiers.find((tier) => tier.isRecommended) ?? nextDetail.optionTiers[0] ?? null;
        setSelectedOptionId(recommended?.id ?? "");
        setSignerName(nextDetail.signerName);
        setSignatureText(nextDetail.signerName);
        setError(null);
      } catch (loadError) {
        if (cancelled) return;
        setError(loadError instanceof Error ? loadError.message : "Failed to load estimate");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [proposalId, tenantSlug, token]);

  const selectedOption = useMemo(
    () => detail?.optionTiers.find((tier) => tier.id === selectedOptionId) ?? null,
    [detail, selectedOptionId],
  );

  async function acceptEstimate() {
    if (!tenantSlug || !proposalId || !token || !selectedOptionId || signatureText.trim().length < 2) {
      setError("Choose an option and provide your typed signature before accepting.");
      return;
    }

    setDecisionState("accepting");
    setError(null);
    try {
      await apiFetch(`/api/public/bookings/estimates/${tenantSlug}/${proposalId}/accept`, {
        method: "POST",
        body: JSON.stringify({
          token,
          selectedOptionId,
          signatureMethod: "typed",
          signatureData: signatureText.trim(),
          signerName: signerName.trim() || undefined,
        }),
      });
      setDecisionState("accepted");
    } catch (acceptError) {
      setDecisionState("idle");
      setError(acceptError instanceof Error ? acceptError.message : "Failed to accept estimate");
    }
  }

  async function declineEstimate() {
    if (!tenantSlug || !proposalId || !token) {
      setError("The estimate link is incomplete.");
      return;
    }

    setDecisionState("declining");
    setError(null);
    try {
      await apiFetch(`/api/public/bookings/estimates/${tenantSlug}/${proposalId}/decline`, {
        method: "POST",
        body: JSON.stringify({ token }),
      });
      setDecisionState("declined");
    } catch (declineError) {
      setDecisionState("idle");
      setError(declineError instanceof Error ? declineError.message : "Failed to decline estimate");
    }
  }

  if (error && !detail) {
    return <main className="mx-auto max-w-3xl px-6 py-16 text-sm text-[var(--color-destructive)]">{error}</main>;
  }

  if (!detail) {
    return (
      <main className="mx-auto flex min-h-screen max-w-3xl items-center justify-center px-6 py-16 text-sm text-[var(--color-muted-foreground)]">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        Loading estimate…
      </main>
    );
  }

  if (decisionState === "accepted") {
    return (
      <main className="mx-auto flex min-h-screen max-w-3xl items-center px-6 py-16">
        <div className="w-full rounded-3xl border border-[var(--color-success)]/30 bg-[var(--color-success)]/10 p-8 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-[var(--color-success)]" />
          <h1 className="mt-4 text-2xl font-semibold text-[var(--color-foreground)]">Estimate accepted</h1>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            Your selection has been signed and the booking request was created automatically.
          </p>
        </div>
      </main>
    );
  }

  if (decisionState === "declined") {
    return (
      <main className="mx-auto flex min-h-screen max-w-3xl items-center px-6 py-16">
        <div className="w-full rounded-3xl border border-[var(--color-border)] bg-[var(--color-background)] p-8 text-center">
          <XCircle className="mx-auto h-10 w-10 text-[var(--color-muted-foreground)]" />
          <h1 className="mt-4 text-2xl font-semibold text-[var(--color-foreground)]">Estimate declined</h1>
          <p className="mt-2 text-sm text-[var(--color-muted-foreground)]">
            The sender can update the offer and send a revised signing link if needed.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(59,130,246,0.08),_transparent_36%),linear-gradient(180deg,#f8fafc_0%,#eef2ff_100%)] px-4 py-10 sm:px-6">
      <div className="mx-auto max-w-5xl space-y-6">
        <section className="rounded-3xl border border-[var(--color-border)] bg-white/90 p-8 shadow-sm backdrop-blur">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-[0.18em] text-[var(--color-muted-foreground)]">Estimate review</div>
              <h1 className="text-3xl font-semibold text-[var(--color-foreground)]">{detail.title}</h1>
              {detail.service ? (
                <p className="text-sm text-[var(--color-muted-foreground)]">
                  {detail.service.serviceName} on {formatDateTime(detail.service.desiredStartAt)} · {detail.service.durationMinutes} minutes
                </p>
              ) : null}
            </div>
            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-sm text-[var(--color-muted-foreground)]">
              <div>Status: <span className="font-medium text-[var(--color-foreground)]">{detail.status}</span></div>
              {detail.validUntil ? <div className="mt-1">Valid until {formatDateTime(detail.validUntil)}</div> : null}
            </div>
          </div>
        </section>

        <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          <section className="space-y-4 rounded-3xl border border-[var(--color-border)] bg-white/90 p-6 shadow-sm backdrop-blur">
            <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Choose your option</h2>
            <div className="grid gap-4">
              {detail.optionTiers.map((tier) => (
                <button
                  key={tier.id}
                  type="button"
                  onClick={() => setSelectedOptionId(tier.id)}
                  className={`rounded-2xl border p-5 text-left transition ${
                    selectedOptionId === tier.id
                      ? "border-[var(--color-primary)] bg-[var(--color-primary)]/5 shadow-sm"
                      : "border-[var(--color-border)] bg-[var(--color-background)] hover:bg-[var(--color-surface)]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-semibold text-[var(--color-foreground)]">{tier.name}</h3>
                        {tier.isRecommended ? (
                          <span className="rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[var(--color-primary)]">
                            Recommended
                          </span>
                        ) : null}
                      </div>
                      {tier.description ? <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">{tier.description}</p> : null}
                    </div>
                    <div className="text-right text-lg font-semibold text-[var(--color-foreground)]">
                      {formatMoney(tier.totalValueCents, detail.currency)}
                    </div>
                  </div>
                </button>
              ))}
            </div>

            <div className="space-y-3 rounded-2xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Pricing details</h2>
              {detail.sections.filter((section) => section.sectionType === "pricing").map((section) => (
                <div key={section.id} className="rounded-xl border border-[var(--color-border)] p-4">
                  <div className="font-medium text-[var(--color-foreground)]">{section.title}</div>
                  {section.pricing?.length ? (
                    <div className="mt-3 space-y-2 text-sm text-[var(--color-muted-foreground)]">
                      {section.pricing.map((row, index) => (
                        <div key={`${section.id}-${index}`} className="flex items-center justify-between gap-4">
                          <span>{row.description} · {row.quantity}× {formatMoney(Math.round(row.unitPrice * 100), detail.currency)}</span>
                          <span className="font-medium text-[var(--color-foreground)]">{formatMoney(Math.round(row.total * 100), detail.currency)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </section>

          <aside className="space-y-4 rounded-3xl border border-[var(--color-border)] bg-white/90 p-6 shadow-sm backdrop-blur">
            <div>
              <h2 className="text-lg font-semibold text-[var(--color-foreground)]">Sign to accept</h2>
              <p className="mt-1 text-sm text-[var(--color-muted-foreground)]">
                Your typed signature records acceptance with timestamp, IP hash, and browser evidence.
              </p>
            </div>

            <label className="space-y-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Signer name</span>
              <input
                type="text"
                value={signerName}
                onChange={(event) => setSignerName(event.target.value)}
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5 text-sm text-[var(--color-foreground)]"
              />
            </label>

            <label className="space-y-2 text-sm">
              <span className="font-medium text-[var(--color-foreground)]">Typed signature</span>
              <input
                type="text"
                value={signatureText}
                onChange={(event) => setSignatureText(event.target.value)}
                placeholder="Type your full name"
                className="w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-background)] px-3 py-2.5 text-sm text-[var(--color-foreground)]"
              />
            </label>

            <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 text-sm text-[var(--color-muted-foreground)]">
              Selected option
              <div className="mt-1 font-medium text-[var(--color-foreground)]">
                {selectedOption ? `${selectedOption.name} · ${formatMoney(selectedOption.totalValueCents, detail.currency)}` : "Choose one option"}
              </div>
            </div>

            {error ? (
              <div className="rounded-xl border border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/10 px-4 py-3 text-sm text-[var(--color-destructive)]">
                {error}
              </div>
            ) : null}

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={() => void acceptEstimate()}
                disabled={decisionState === "accepting" || decisionState === "declining"}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-[var(--color-primary)] px-4 py-3 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {decisionState === "accepting" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Accept and sign
              </button>
              <button
                type="button"
                onClick={() => void declineEstimate()}
                disabled={decisionState === "accepting" || decisionState === "declining"}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--color-border)] px-4 py-3 text-sm font-medium text-[var(--color-foreground)] hover:bg-[var(--color-surface)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {decisionState === "declining" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Decline
              </button>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}
